import React, { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import "./ModelViewer.css";
import StatsComponent from "../../components/StatsComponent";
import { useMultiExpConfig } from "../../context/MultiExpConfigProvider";
import  Loader  from '../../molecules/SpinningLoader';

const ModelViewer = ({ modelUrls }) => {


  console.debug("ModelViewer: modelUrls received =", modelUrls);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [debugMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("debug") === "true";
  });

  const [useDefault] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const value = decodeURIComponent(params.get("useDefault") || "");
    console.debug("useDefault param value:", value);
    return value.toLowerCase() === "true";
  });
  const [statsMap, setStatsMap] = useState(new Map([]));

  const transformModelUrl = (url) => {
    console.debug("Transforming model URL: ", url);
    console.debug("useDefault value: ", useDefault);

    if (useDefault === true) {
      console.debug("Using original S3 URL");
      return url;
    }
    console.debug("Using Cloudfront URL");
    return url.replace(
      "https://ceimages.s3.amazonaws.com/",
      "https://ceimages.s3.amazonaws.com/"
    )
  };

  const transformedModelUrls = modelUrls.map(transformModelUrl);
  console.debug("ModelViewer: transformedModelUrls after map =", transformedModelUrls);
  

  const [isInteractiveMode, setIsInteractiveMode] = useState(false);

  const { setIsModelViewerInteractive } = useMultiExpConfig();

  // references to the model and interactive model
  const mainModelRef = useRef(null);
  const interactiveModelRef = useRef(null);

  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isInteractiveModelLoaded, setIsInteractiveModelLoaded] = useState(false);

  useEffect(() => {
    setIsModelLoaded(false);

    const modelViewer = mainModelRef.current;

    const handleLoad = () => {
      console.debug("Main model loaded.");
      setIsModelLoaded(true);
    };

    const handleError = (error) => {
      console.error("Error loading main model:", error);
      setIsModelLoaded(true); 
    };

    if (modelViewer) {
      modelViewer.addEventListener('load', handleLoad);
      modelViewer.addEventListener('error', handleError);
    }

    return () => {
      if (modelViewer) {
        modelViewer.removeEventListener('load', handleLoad);
        modelViewer.removeEventListener('error', handleError);
      }
    };

  }, [currentIndex]);


  useEffect(() => {
    if (isInteractiveMode) {
      setIsInteractiveModelLoaded(false);
      const modelViewer = interactiveModelRef.current; 

      const handleLoad = () => {
        setIsInteractiveModelLoaded(true);
      };

      const handleError = (error) => {
        console.error("Error loading interactive model:", error);
        setIsInteractiveModelLoaded(true); // Hide loader
      };

      if (modelViewer) {
        modelViewer.addEventListener('load', handleLoad);
        modelViewer.addEventListener('error', handleError);
      }

      return () => {
        if (modelViewer) {
          modelViewer.removeEventListener('load', handleLoad);
          modelViewer.removeEventListener('error', handleError);
        }
      };
    }
  }, [isInteractiveMode, currentIndex]);

  const currentModelToDisplay = transformedModelUrls[currentIndex];


  const addStat = (key, value) => {
    setStatsMap((prev) => {
      const updated = new Map(prev);
      updated.set(key, value);
      return updated;
    });
  };


  useEffect(() => {
    setIsModelViewerInteractive(isInteractiveMode);
  }, [isInteractiveMode, setIsModelViewerInteractive]);

  useEffect(() => {
    if (debugMode && !isInteractiveMode) {
      const modelViewer = document.querySelector(`model-viewer[key="main-viewer-${currentIndex}"]`);
      if (modelViewer) {
        const startTime = performance.now();

        const handleLoad = () => {
          const loadTime = performance.now() - startTime;
          addStat(`Model ${currentIndex + 1} Load Time`, `${loadTime.toFixed(2)}ms`);
        };
        const handleError = (error) => {
          addStat(`Model ${currentIndex + 1} Error`, error.detail || "Unknown error");
        };
        const handleArStatus = (event) => {
          addStat(`Model ${currentIndex + 1} AR Status`, event.detail.status);
        };

        modelViewer.removeEventListener("load", handleLoad);
        modelViewer.removeEventListener("error", handleError);
        modelViewer.removeEventListener("ar-status", handleArStatus);

        modelViewer.addEventListener("load", handleLoad);
        modelViewer.addEventListener("error", handleError);
        modelViewer.addEventListener("ar-status", handleArStatus);
      }
    }
  }, [currentIndex, debugMode, isInteractiveMode, currentModelToDisplay]);

  const swipeHandlers = useSwipeable({
    onSwipedUp: () => {
      if (!isInteractiveMode && transformedModelUrls.length > 1) {
        setCurrentIndex((prev) => (prev + 1) % transformedModelUrls.length);
      }
    },
    onSwipedDown: () => {
      if (!isInteractiveMode && transformedModelUrls.length > 1) {
        setCurrentIndex(
          (prev) => (prev - 1 + transformedModelUrls.length) % transformedModelUrls.length
        );
      }
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
    trackMouse: true,
  });



  const handleHotspotClick = (e) => {
    e.stopPropagation();
    setIsInteractiveMode(true);

    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsInteractiveMode(false);
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
    }
  }



  return (
    <>
      <div {...(!isInteractiveMode && transformedModelUrls.length > 1 ? swipeHandlers : {})} className="viewer-container">

        {!isModelLoaded && (
          <div className="model-loading-container">
            <Loader />
          </div>
        )}

        <model-viewer
          key={`main-viewer-${currentIndex}`}
          src={currentModelToDisplay}
          ref={mainModelRef}
          alt={`3D Model ${currentIndex + 1}`}
          {...(isInteractiveMode ? { "camera-controls": true } : {})}
          // ar={false}
          className="model-viewer"
          camera-orbit="0deg 75deg 2m"
          style={{ width: "100%", height: "100%", opacity: isModelLoaded ? 1 : 0 }} // Hide until loaded
          crossorigin="anonymous"
          loading="eager"
          reveal="auto"
        />


        {isModelLoaded && !isInteractiveMode && (
          <div className="hotspot-wrapper" onClick={handleHotspotClick}>
            <button
              slot="hotspot-1"
              data-position="0 0 0.3m"
              data-normal="0 0 1"
              onClick={handleHotspotClick}
              className="hotspot-blink-dot"
              aria-label="Activate Interactive 3D Mode"
            ></button>
          </div>
        )}

      </div>

      {isInteractiveMode && (
        <div className="interactive-overlay" onClick={handleOverlayClick}>
          <div className="interactive-content">
            {/* Loader for the interactive model */}
            {!isInteractiveModelLoaded && (
              <div className="model-loading-container">
                <Loader />
              </div>
            )}

            <model-viewer
              key={`interactive-viewer-${currentIndex}`}
              src={currentModelToDisplay}
              alt={`3D Model ${currentIndex + 1} Interactive`}
              auto-rotate
              ref={interactiveModelRef}
              camera-controls
              ar
              ar-modes="webxr scene-viewer quick-look"
              className="interactive-model-viewer"
              camera-orbit="0deg 75deg 2m"
              style={{ width: "100%", height: "100%", opacity: isInteractiveModelLoaded ? 1 : 0 }} // Hide until loaded
              onTouchStart={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              shadow-intensity="5"
              exposure="1"
              environment-image="https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/2k/studio_small_03_2k.hdr"
            // REMOVE onLoad and onError from here
            />
          </div>
        </div>
      )}


      {debugMode && <StatsComponent data={statsMap} />}
    </>
  );
};

export default ModelViewer;